import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { workouts } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, Badge, SkeletonCard, EmptyState } from '../../components/ui'

const TYPE_COLORS = {
  strength: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-800/40' },
  cardio: { bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-800/40' },
  hiit: { bg: 'bg-orange-900/30', text: 'text-orange-400', border: 'border-orange-800/40' },
  circuit: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-800/40' },
  full_body: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-800/40' },
  upper: { bg: 'bg-indigo-900/30', text: 'text-indigo-400', border: 'border-indigo-800/40' },
  lower: { bg: 'bg-teal-900/30', text: 'text-teal-400', border: 'border-teal-800/40' },
  core: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-800/40' },
  flexibility: { bg: 'bg-pink-900/30', text: 'text-pink-400', border: 'border-pink-800/40' },
}

function WorkoutCard({ workout }) {
  const typeStyle = TYPE_COLORS[workout.workoutType] || { bg: 'bg-dial-elevated', text: 'text-indices-medium', border: 'border-dial-border' }

  return (
    <Link to={`/workouts/${workout.id}`}>
      <Card hover>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-medium text-indices-bright">{workout.name}</h3>
            {workout.description && (
              <p className="text-sm text-indices-medium mt-1 line-clamp-2">{workout.description}</p>
            )}
          </div>
          <span className={`px-2 py-1 text-xs rounded-full capitalize border ${typeStyle.bg} ${typeStyle.text} ${typeStyle.border}`}>
            {workout.workoutType?.replace('_', ' ')}
          </span>
        </div>
        <div className="flex items-center gap-4 mt-3 text-sm text-indices-muted">
          {workout.durationMinutes && (
            <span>{workout.durationMinutes} min</span>
          )}
          {workout.difficulty && (
            <span className="capitalize">{workout.difficulty}</span>
          )}
          {workout.exercises?.length > 0 && (
            <span>{workout.exercises.length} exercises</span>
          )}
        </div>
      </Card>
    </Link>
  )
}

export default function WorkoutListPage() {
  const { data: workoutList, isLoading } = useQuery({
    queryKey: ['workouts'],
    queryFn: workouts.list,
  })

  return (
    <PageLayout>
      <PageHeader
        title="Workouts"
        showBack
        backTo="/"
        actions={
          <Link
            to="/workouts/new"
            className="p-2 text-lume-teal hover:opacity-80"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </Link>
        }
      />

      <div className="px-4 py-4 space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : workoutList?.length === 0 ? (
          <EmptyState
            title="No workout templates yet"
            description="Create your first workout template to assign to clients."
            action={
              <Link to="/workouts/new" className="text-lume-teal font-medium mt-2 inline-block hover:opacity-80">
                Create your first workout
              </Link>
            }
          />
        ) : (
          workoutList?.map(workout => (
            <WorkoutCard key={workout.id} workout={workout} />
          ))
        )}
      </div>
    </PageLayout>
  )
}
